// DynamicFormRenderer.tsx
import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "../form/FormContext";
import { FormBuilder } from '../form/FormBuilder';

// Minimal type definitions for clarity
interface FormField {
  key: string;
  label: string;
  id: number;
  section: string;
  order: number;
  component: string;
  component_props: Record<string, any>;
  options: Array<{ label: string; value: string }> | null;
  is_required: boolean;
  helper_text: string;
  placeholder?: string | null;
  min_value?: string | null;
  max_value?: string | null;
  step_value?: string | null;
  disabled?: boolean;
  rows?: number | null;
}

interface ExtraField {
  key: string;
  label: string;
  value: string;
  type: string;
}

interface DynamicFormRendererProps {
  fields: FormField[];
  onSubmit: (values: any) => void;
  initialValues?: Record<string, any>;
  gridConfig?: { mobile: number; desktop: number };
  acceptExtraFields?: boolean;
  initExtraFields?: ExtraField[];
}

const DynamicFormRenderer: React.FC<DynamicFormRendererProps> = ({
  fields,
  onSubmit,
  initialValues,
  acceptExtraFields = false,
  initExtraFields = [],
  ...rest
}) => {
  const schema = {
    type: 'object' as const,
    properties: fields.reduce((acc, field: FormField) => {
      acc[field.id] = {
        ...field,
        componentProps: field.component_props || {},
        helperText: field.helper_text,
        required: field.is_required,
        placeholder: field.placeholder,
      };
      return acc;
    }, {} as Record<string, any>),
  };

  return (
    <div className="pt-4">
      <FormProvider initialValues={initialValues} onSubmit={onSubmit}>
        <FormBuilder
          schema={schema}
          onSubmit={onSubmit}
          initialValues={initialValues}
          acceptExtraFields={acceptExtraFields}
          initExtraFields={initExtraFields}
          {...rest}
        />
      </FormProvider>
    </div>
  );
};

export default DynamicFormRenderer;